import { ChangeDetectorRef, Component, OnInit } from '@angular/core';

import { catchError, map, mergeMap, Observable, of, zip } from 'rxjs';

import { IUserData, TwcTopconOneService, TwcUserOrganization } from '@topcon/twc-angular';

import { AuthorizationService, MagnetService, UserService } from '@mirage-projects/cw-commons';

import { AppSharedService } from './app-shared.service';
import { AppService } from './app.service';

/**
 * Fix to solve not finishing session after failed ping
 * This should be solved by TWC on next release
 * https://tps-jira.topcon.com/browse/COLW-13971
 */
const TwcTopconOneServicePrototype = TwcTopconOneService.prototype as any;
TwcTopconOneServicePrototype._checkAuth = function (this: any): any {
    const access = this.twcUserAccess;
    const headers = this._getHeaders(access);

    return this._http
        .get(`${this._api_host}/${this._apiVersion}/ping`, {
            params: { authenticate: `1` },
            headers,
        })
        .pipe(
            map((res: any) => !!res?.success),
            catchError(() => of(false))
        );
};

/** This is the root component for Collage Web application */
@Component({
    selector: 'cw-root',
    templateUrl: './app.component.html',
    standalone: false,
})
export class AppComponent implements OnInit {
    loadTopBar = false;
    organizationId = '';

    constructor(
        private userService: UserService,
        private authorizationService: AuthorizationService,
        private changeDetectorRef: ChangeDetectorRef,
        private topconOneService: TwcTopconOneService,
        private magnetService: MagnetService,
        private appService: AppService,
        public appSharedService: AppSharedService
    ) {}

    ngOnInit(): void {
        this.appSharedService.init(this.changeDetectorRef);
        this.topconOneService.logged$.subscribe((twcUserLogged: IUserData) => {
            if (!twcUserLogged) {
                this.authorizationService.appLogout();
                return;
            }
            if (twcUserLogged) {
                this.initUserLogged(twcUserLogged);
            }

            this.organization$.subscribe(([organization, stored]) => {
                if (organization.id !== stored?.id) {
                    this.topconOneService.setCurrentOrganization(organization);

                    if (stored) {
                        this.clearAndRefresh();
                    }
                }

                if (!this.organizationId) {
                    this.organizationId = organization?.id;
                    return;
                }

                if (organization?.id === this.organizationId) {
                    return;
                }

                this.clearAndRefresh();
            });
        });
    }

    get showContent(): boolean {
        return this.appSharedService.isUserLogged || this.appService.isHome();
    }

    get showTopbar(): boolean {
        return this.loadTopBar;
    }

    get showSidebar(): boolean {
        return this.appSharedService.isUserLogged;
    }

    get showFooter(): boolean {
        return !this.appService.isViewerShown();
    }

    /*
    Init user logged for CW
    */
    private initUserLogged(twcUserLogged: IUserData): void {
        const storedUser = this.authorizationService.getCurrentUser();
        const userId = storedUser.username && this.authorizationService.getSupportUser() ? storedUser.username : twcUserLogged.id;

        this.userService.get(userId).subscribe((userData) => {
            const user = this.authorizationService.userResponseToUser(userData);
            this.appSharedService.setUserLogged(user);
            this.changeDetectorRef.detectChanges();
            this.loadTopBar = true;
        });
    }

    private clearAndRefresh(): void {
        this.authorizationService.clearCredentials();
        window.location.href = this.magnetService.getCollageLoginUrl();
    }

    /**
     * The current organization comes now from auth API in the defaulOrganization observable.
     * If there is no change, the currentOrganization returns a localStorage catched value.
     * If the Organization is changed from other environment (qa, alpha) this values will be different.
     * At the sometime server will return values from the previous organization till a new auth is done
     * So this observable, returns together the default and the stored values
     * so later if they are different we can force a reauth.
     */
    private get organization$(): Observable<[organization: TwcUserOrganization, stored?: TwcUserOrganization]> {
        return this.topconOneService.defaultOrganization$.pipe(
            mergeMap((defaultOrganization) => {
                const currentOrganization$ = this.topconOneService.currentOrganization$;
                return zip([of(defaultOrganization), currentOrganization$]);
            })
        );
    }
}
